import React from 'react';
import BaseFilter from '../base/BaseFilter';
import CustomSelect from '../base/CustomSelect';
import VietnameseDatePicker from '../documents/VietnameseDatePicker';

export const REPORT_PERIOD_OPTIONS = [
    { value: '', label: 'Tất cả thời gian / Tùy chọn' },
    { value: 'HOM_NAY', label: 'Hôm nay' },
    { value: 'TUAN_NAY', label: 'Tuần này' },
    { value: 'THANG_NAY', label: 'Tháng này' },
    { value: 'THANG_TRUOC', label: 'Tháng trước' },
    { value: 'QUY_NAY', label: 'Quý này' },
    { value: 'QUY_TRUOC', label: 'Quý trước' },
    { value: 'NAM_NAY', label: 'Năm nay' },
    { value: 'NAM_TRUOC', label: 'Năm trước' },
];

export const SLA_STATUS_OPTIONS = [
    { value: '', label: 'Tất cả' },
    { value: 'ON_TIME', label: 'Còn hạn' },
    { value: 'NEAR_DUE', label: 'Sắp đến hạn' },
    { value: 'OVERDUE', label: 'Quá hạn' },
    { value: 'PENDING_EXTENSION', label: 'Chờ gia hạn' },
    { value: 'EXTENDED', label: 'Đã gia hạn' },
    { value: 'REJECTED_EXTENSION', label: 'Từ chối gia hạn' },
];

export const getPeriodDateRange = (period) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const formatDate = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    switch (period) {
        case 'HOM_NAY': {
            const todayStr = formatDate(now);
            return { startDate: todayStr, endDate: todayStr };
        }
        case 'TUAN_NAY': {
            const day = now.getDay();
            const diffToMonday = day === 0 ? -6 : 1 - day;
            const monday = new Date(year, month, now.getDate() + diffToMonday);
            const sunday = new Date(year, month, monday.getDate() + 6);
            return { startDate: formatDate(monday), endDate: formatDate(sunday) };
        }
        case 'THANG_NAY': {
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        case 'THANG_TRUOC': {
            const firstDay = new Date(year, month - 1, 1);
            const lastDay = new Date(year, month, 0);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        case 'QUY_NAY': {
            const currentQuarter = Math.floor(month / 3);
            const firstDay = new Date(year, currentQuarter * 3, 1);
            const lastDay = new Date(year, (currentQuarter + 1) * 3, 0);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        case 'QUY_TRUOC': {
            const currentQuarter = Math.floor(month / 3);
            const firstDay = new Date(year, (currentQuarter - 1) * 3, 1);
            const lastDay = new Date(year, currentQuarter * 3, 0);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        case 'NAM_NAY': {
            const firstDay = new Date(year, 0, 1);
            const lastDay = new Date(year, 11, 31);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        case 'NAM_TRUOC': {
            const firstDay = new Date(year - 1, 0, 1);
            const lastDay = new Date(year - 1, 11, 31);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        default:
            return { startDate: '', endDate: '' };
    }
};

const ReportFilter = ({ 
    onFilter, 
    onReset, 
    filters = {}, 
    pagination = {}, 
    statusReport = {},
    reportAreas = [],
    extent = {},
    extraActions = null
}) => {
    const initialFilters = {
        trangThai: filters.trangThai || '',
        tinhTrang: filters.tinhTrang || filters.slaStatus || '',
        idLinhVucPhanAnh: filters.idLinhVucPhanAnh || '',
        khuPho: filters.khuPho || '',
        mucDo: filters.mucDo || '',
        maPhanAnh: filters.maPhanAnh || '',
        sortTime: filters.sortTime || 'desc',
        pageSize: pagination.pageSize || 10,
        kyBaoCao: filters.kyBaoCao || '',
        startDate: filters.startDate || '',
        endDate: filters.endDate || '',
    };

    const filterFields = [
        {
            name: 'maPhanAnh',
            label: 'Mã phản ánh',
            type: 'search',
            placeholder: 'Tìm theo mã phản ánh...'
        },
        {
            name: 'trangThai',
            label: 'Trạng thái',
            type: 'select',
            options: [
                { value: '', label: 'Tất cả' },
                ...(statusReport && typeof statusReport === 'object' ? Object.entries(statusReport).map(([key, value]) => ({
                    value: value,
                    label: value
                })) : [])
            ]
        },
        {
            name: 'tinhTrang',
            label: 'Tình trạng',
            type: 'select',
            options: SLA_STATUS_OPTIONS
        },
        {
            name: 'idLinhVucPhanAnh',
            label: 'Lĩnh vực',
            type: 'select',
            options: [
                { value: '', label: 'Tất cả' },
                ...reportAreas.map(area => ({
                    value: area.id,
                    label: area.ten
                }))
            ]
        },
        {
            name: 'khuPho',
            label: 'Khu phố',
            type: 'select',
            options: [
                { value: '', label: 'Tất cả khu phố' },
                ...Array.from({ length: 45 }, (_, i) => {
                    const num = String(i + 1).padStart(2, '0');
                    return { value: `KP ${num}`, label: `Khu phố ${num}` };
                })
            ]
        },
        {
            name: 'mucDo',
            label: 'Mức độ',
            type: 'select',
            options: [
                { value: '', label: 'Tất cả' },
                { value: 'Thông thường', label: 'Thông thường' },
                { value: 'Khẩn cấp', label: 'Khẩn cấp' },
            ]
        },
        {
            name: 'sortTime',
            label: 'Sắp xếp theo thời gian',
            type: 'select',
            options: [
                { value: 'desc', label: 'Mới nhất trước' },
                { value: 'asc', label: 'Cũ nhất trước' }
            ]
        },
        {
            name: 'pageSize',
            label: 'Số bản ghi',
            type: 'select',
            options: [
                { value: 5, label: '5 bản ghi' },
                { value: 10, label: '10 bản ghi' },
                { value: 20, label: '20 bản ghi' },
                { value: 50, label: '50 bản ghi' }
            ]
        },
        {
            name: 'kyBaoCao',
            label: 'Kỳ báo cáo',
            type: 'custom',
            render: ({ filters: currentFilters, setFilters }) => (
                <CustomSelect
                    value={currentFilters.kyBaoCao || ''}
                    onChange={(val) => {
                        const dateRange = getPeriodDateRange(val);
                        setFilters((prev) => ({
                            ...prev,
                            kyBaoCao: val,
                            startDate: dateRange.startDate,
                            endDate: dateRange.endDate
                        }));
                    }}
                    options={REPORT_PERIOD_OPTIONS}
                    placeholder="Tất cả thời gian / Tùy chọn"
                    className="w-full"
                />
            )
        },
        {
            name: 'dateRange',
            label: 'Từ ngày',
            type: 'custom',
            render: ({ filters: currentFilters, setFilters }) => (
                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <div className="relative flex-1 min-w-[120px]">
                        <VietnameseDatePicker
                            value={currentFilters.startDate || ''}
                            maxDate={currentFilters.endDate || undefined}
                            onChange={(isoVal) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    startDate: isoVal,
                                    kyBaoCao: ''
                                }));
                            }}
                            placeholder="dd/MM/yyyy"
                        />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap px-1">
                        Đến ngày
                    </span>
                    <div className="relative flex-1 min-w-[120px]">
                        <VietnameseDatePicker
                            value={currentFilters.endDate || ''}
                            minDate={currentFilters.startDate || undefined}
                            onChange={(isoVal) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    endDate: isoVal,
                                    kyBaoCao: ''
                                }));
                            }}
                            placeholder="dd/MM/yyyy"
                        />
                    </div>
                </div>
            )
        }
    ];

    const handleFilter = (newFilters) => {
        onFilter({
            trangThai: newFilters.trangThai || '',
            tinhTrang: newFilters.tinhTrang || '',
            idLinhVucPhanAnh: newFilters.idLinhVucPhanAnh || '',
            khuPho: newFilters.khuPho || '',
            mucDo: newFilters.mucDo || '',
            maPhanAnh: (newFilters.maPhanAnh || '').trim(),
            sortTime: newFilters.sortTime || 'desc',
            pageSize: Number(newFilters.pageSize) || 10,
            kyBaoCao: newFilters.kyBaoCao || '',
            startDate: newFilters.startDate || '',
            endDate: newFilters.endDate || '',
        });
    };

    const handleReset = () => {
        onReset();
    };

    return (
        <BaseFilter
            fields={filterFields}
            onFilter={handleFilter}
            onReset={handleReset}
            initialFilters={initialFilters}
            searchPlaceholder="Tìm kiếm phản ánh..."
            showSearchButton={true}
            extraActions={extraActions}
        />
    );
};

export default ReportFilter;
